// ============================================================================
// INDIVIDUAL RESULTS COMPONENT
// Created by Salvador Janeiro
// ============================================================================

import React from 'react';
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
  Leaf, TrendingDown, Award, Download, Target, 
  Droplet, Trash2, Globe2, AlertCircle, CheckCircle 
} from 'lucide-react';
import { 
  calculateImpactEquivalents, 
  calculateWaterEquivalents,
  getSustainabilityLevel 
} from './calculations';
import { BENCHMARKS } from './data-constants';

const IndividualResults = ({ results, onReset }) => {
  const { carbonTotal, waterTotal, plasticTotal, breakdown, score, answers } = results;

  // Prepare chart data
  const breakdownData = [
    { category: 'Transportation', value: breakdown.transportation / 1000, color: '#E63946' },
    { category: 'Home Energy', value: breakdown.homeEnergy / 1000, color: '#FFB020' },
    { category: 'Diet', value: breakdown.diet / 1000, color: '#2A9D8F' },
    { category: 'Fashion', value: breakdown.fashion / 1000, color: '#457B9D' },
    { category: 'Waste', value: breakdown.waste / 1000, color: '#A8DADC' },
    { category: 'Consumption', value: breakdown.consumption / 1000, color: '#F1FAEE' },
  ].filter(item => item.value > 0);

  // Benchmarks
  const percentOfEU = ((carbonTotal / BENCHMARKS.carbon.euAverage) * 100).toFixed(0);
  const percentOfUN = ((carbonTotal / BENCHMARKS.carbon.unTarget) * 100).toFixed(0);

  // Equivalents
  const impactEquiv = calculateImpactEquivalents(carbonTotal);
  const waterEquiv = calculateWaterEquivalents(waterTotal);
  
  // Sustainability level
  const { level, color, badge } = getSustainabilityLevel(score);

  // Generate personalized narrative
  const generateNarrative = () => {
    const narratives = [];
    const improvements = [];

    // Overall assessment
    if (score >= 70) {
      narratives.push("You're doing great on your sustainability journey! Your lifestyle choices show strong environmental consciousness.");
    } else if (score >= 40) {
      narratives.push("You're making some good sustainability choices, but there's room for improvement in several areas.");
    } else {
      narratives.push("Your current lifestyle has a significant environmental impact. The good news is that small changes can make a big difference!");
    }

    // Positive behaviors
    const positives = [];
    if (answers.commute === 'walking' || answers.commute === 'publicTransit') {
      positives.push("eco-friendly commuting");
    }
    if (answers.diet === 'vegan' || answers.diet === 'vegetarian') {
      positives.push("plant-based diet");
    }
    if (answers.recycling === 'always') {
      positives.push("excellent recycling habits");
    }
    if (answers.singleUsePlastic === 'hardlyEver') {
      positives.push("minimal single-use plastics");
    }
    if (answers.energySource === 'renewable') {
      positives.push("renewable energy use");
    }

    if (positives.length > 0) {
      narratives.push(`✅ You excel in: ${positives.join(', ')}.`);
    }

    // Areas for improvement
    if (breakdown.transportation > 2000) {
      improvements.push({
        area: "Transportation",
        impact: "Your travel choices contribute significantly to your carbon footprint.",
        suggestion: answers.flights !== 'never' 
          ? "Consider reducing air travel or choosing train alternatives when possible. Each flight avoided can save huge emissions."
          : "Try using public transit, carpooling, or cycling more often for your daily commute."
      });
    }

    if (answers.diet === 'meatHeavy') {
      improvements.push({
        area: "Diet",
        impact: "Your meat-heavy diet has a substantial environmental impact. Beef production uses ~15,000 liters of water per kg.",
        suggestion: "Try 'Meatless Mondays' or swap beef for chicken or plant-based alternatives a few days per week. This could reduce your footprint by 20-30%."
      });
    }

    if (answers.clothingFrequency === 'frequently') {
      improvements.push({
        area: "Fashion",
        impact: "Frequent clothing purchases contribute to fast fashion waste. The fashion industry is responsible for 10% of global emissions.",
        suggestion: "Consider buying fewer, higher-quality items, or explore second-hand fashion. Each item you don't buy saves thousands of liters of water."
      });
    }

    if (answers.singleUsePlastic === 'often') {
      improvements.push({
        area: "Plastic Use",
        impact: `You generate approximately ${plasticTotal} kg of plastic waste annually, above the global average of 28 kg.`,
        suggestion: "Switch to reusable bottles, bags, and containers. This simple change can nearly eliminate your plastic waste."
      });
    }

    return { narratives, improvements };
  };

  const { narratives, improvements } = generateNarrative();

  // Generate recommendations
  const generateRecommendations = () => {
    const recs = [];
    
    if (carbonTotal > BENCHMARKS.carbon.unTarget) {
      recs.push({
        title: "Reduce Your Carbon Footprint",
        description: `You're currently at ${carbonTotal} tons CO₂/year. Aim for ${BENCHMARKS.carbon.unTarget} tons to meet climate goals.`,
        actions: [
          "Switch to renewable energy for your home",
          "Reduce meat consumption, especially beef",
          "Choose public transit or cycling over driving",
          "Minimize air travel or offset unavoidable flights"
        ]
      });
    }

    if (waterTotal > 2000000) {
      recs.push({
        title: "Conserve Water Resources",
        description: "Your water footprint is high, mainly from diet and fashion choices.",
        actions: [
          "Reduce beef and dairy consumption",
          "Buy fewer new clothes, choose second-hand",
          "Install water-efficient fixtures at home",
          "Support brands with water-conscious practices"
        ]
      });
    }

    if (plasticTotal > 28) {
      recs.push({
        title: "Minimize Plastic Waste",
        description: `At ${plasticTotal} kg/year, you're above the global average of 28 kg.`,
        actions: [
          "Carry reusable water bottle and shopping bags",
          "Choose products with minimal packaging",
          "Avoid single-use plastics when possible",
          "Support plastic-free alternatives"
        ]
      });
    }

    return recs;
  };

  const recommendations = generateRecommendations();

  // Export functionality
  const exportReport = () => {
    const report = `
SUSTAINABILITY ASSESSMENT REPORT
Generated: ${new Date().toLocaleDateString()}

OVERALL SUSTAINABILITY SCORE: ${score}/100 (${level})

CARBON FOOTPRINT: ${carbonTotal} tons CO₂e per year
- Target for 1.5°C pathway: ${BENCHMARKS.carbon.unTarget} tons/year
- Your footprint: ${percentOfUN}% of target
- EU Average: ${BENCHMARKS.carbon.euAverage} tons/year
- Your footprint: ${percentOfEU}% of EU average

BREAKDOWN:
${breakdownData.map(item => `- ${item.category}: ${item.value.toFixed(2)} tons CO₂e`).join('\n')}

WATER FOOTPRINT: ${waterTotal.toLocaleString()} liters per year
- Equivalent to ${waterEquiv.pools} Olympic swimming pools
- Largest contributors: diet and fashion choices

PLASTIC WASTE: ${plasticTotal} kg per year
- Global average: 28 kg per person
- Status: ${plasticTotal > 28 ? 'Above' : 'Below'} average

KEY INSIGHTS:
${narratives.join('\n\n')}

AREAS FOR IMPROVEMENT:
${improvements.map(imp => `
${imp.area}:
${imp.impact}
Recommendation: ${imp.suggestion}
`).join('\n')}

RECOMMENDED ACTIONS:
${recommendations.map(rec => `
${rec.title}
${rec.description}
${rec.actions.map(action => `• ${action}`).join('\n')}
`).join('\n')}

---
Created by Salvador Janeiro
Sustainability Self-Assessment Platform
    `.trim();

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sustainability-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-teal-50 p-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Your Sustainability Results</h1>
          <p className="text-gray-600">A comprehensive analysis of your environmental impact</p>
        </div>

        {/* Overall Score Banner */}
        <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-2xl shadow-2xl p-8 mb-8">
          <div className="text-center">
            <div className="text-6xl mb-4">{badge}</div>
            <h2 className="text-3xl font-bold mb-2">Sustainability Score: {score}/100</h2>
            <p className="text-xl opacity-90">Level: {level}</p>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Carbon Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Carbon Footprint</h3>
              <Globe2 className="text-green-600" size={32} />
            </div>
            <div className="text-4xl font-bold text-gray-800 mb-2">
              {carbonTotal} <span className="text-2xl">tons</span>
            </div>
            <div className="text-sm text-gray-600 mb-4">CO₂e per year</div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>vs UN Target ({BENCHMARKS.carbon.unTarget}t):</span>
                <span className={parseFloat(carbonTotal) > BENCHMARKS.carbon.unTarget ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                  {percentOfUN}%
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>vs EU Average ({BENCHMARKS.carbon.euAverage}t):</span>
                <span className={parseFloat(carbonTotal) > BENCHMARKS.carbon.euAverage ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                  {percentOfEU}%
                </span>
              </div>
            </div>
          </div>

          {/* Water Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Water Footprint</h3>
              <Droplet className="text-blue-600" size={32} />
            </div>
            <div className="text-4xl font-bold text-gray-800 mb-2">
              {waterEquiv.cubicMeters} <span className="text-2xl">m³</span>
            </div>
            <div className="text-sm text-gray-600 mb-4">{waterTotal.toLocaleString()} liters per year</div>
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <span>≈ {waterEquiv.pools} Olympic pools</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <span>≈ {impactEquiv.trees} trees needed annually</span>
              </div>
            </div>
          </div>

          {/* Plastic Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Plastic Waste</h3>
              <Trash2 className="text-purple-600" size={32} />
            </div>
            <div className="text-4xl font-bold text-gray-800 mb-2">
              {plasticTotal} <span className="text-2xl">kg</span>
            </div>
            <div className="text-sm text-gray-600 mb-4">per year</div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Global average:</span>
                <span className="font-semibold">28 kg</span>
              </div>
              <div className={`text-sm font-semibold ${plasticTotal > 28 ? 'text-red-600' : 'text-green-600'}`}>
                {plasticTotal > 28 ? `${(((plasticTotal - 28) / 28) * 100).toFixed(0)}% above` : `${(((28 - plasticTotal) / 28) * 100).toFixed(0)}% below`} average
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Pie Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Carbon Emissions Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={breakdownData}
                  dataKey="value"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.category}: ${((entry.value / carbonTotal) * 100).toFixed(0)}%`}
                >
                  {breakdownData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value.toFixed(2)} tons CO₂e`} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Category Impact</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={breakdownData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} fontSize={12} />
                <YAxis label={{ value: 'tons CO₂e', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => `${value.toFixed(2)} tons`} />
                <Bar dataKey="value" fill="#2A9D8F" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Narrative Analysis */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Award className="mr-3 text-green-600" size={28} />
            Your Sustainability Story
          </h3>
          
          <div className="space-y-6">
            {narratives.map((narrative, index) => (
              <p key={index} className="text-gray-700 text-lg leading-relaxed">
                {narrative}
              </p>
            ))}

            {improvements.length > 0 && (
              <div className="mt-8">
                <h4 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <TrendingDown className="mr-2 text-orange-600" size={24} />
                  Areas for Improvement
                </h4>
                <div className="space-y-6">
                  {improvements.map((improvement, index) => (
                    <div key={index} className="border-l-4 border-orange-500 pl-4 py-2">
                      <h5 className="font-semibold text-gray-800 mb-2">{improvement.area}</h5>
                      <p className="text-gray-600 mb-2">{improvement.impact}</p>
                      <p className="text-green-700 italic">💡 {improvement.suggestion}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl shadow-lg p-8 mb-8 border-2 border-green-200">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <Target className="mr-3 text-green-600" size={28} />
              Your Action Plan
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {recommendations.map((rec, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                  <h4 className="font-bold text-lg text-gray-800 mb-2">{rec.title}</h4>
                  <p className="text-gray-600 text-sm mb-4">{rec.description}</p>
                  <ul className="space-y-2">
                    {rec.actions.map((action, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <CheckCircle size={16} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Impact Equivalents */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Understanding Your Impact</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-white rounded-lg shadow">
              <div className="text-5xl mb-3">🌳</div>
              <div className="text-3xl font-bold text-green-700 mb-2">{impactEquiv.trees}</div>
              <p className="text-sm text-gray-600">trees needed to offset your annual CO₂</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow">
              <div className="text-5xl mb-3">🚗</div>
              <div className="text-3xl font-bold text-blue-700 mb-2">{impactEquiv.kmDriven}</div>
              <p className="text-sm text-gray-600">km driven equivalent (avg car)</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow">
              <div className="text-5xl mb-3">🏊</div>
              <div className="text-3xl font-bold text-blue-700 mb-2">{waterEquiv.pools}</div>
              <p className="text-sm text-gray-600">Olympic pools of water used</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        {score < 70 && (
          <div className="bg-blue-600 text-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-4">
              <AlertCircle size={32} className="flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">Need Guidance on Your Sustainability Journey?</h3>
                <p className="text-lg opacity-90 mb-4">
                  Making sustainable changes can feel overwhelming. Consider reaching out to a sustainability 
                  professional for personalized advice and a customized action plan.
                </p>
                <div className="bg-white text-gray-800 p-6 rounded-lg">
                   <p className="font-semibold text-xl mb-2">Contact Salvador Janeiro</p>
                   <p className="text-sm mb-3">AI & Sustainability Expert</p>
                   <p className="text-sm text-gray-600 mb-4">
                    Expert guidance for individuals seeking to reduce their environmental impact
                   </p>
                   <div className="flex flex-col sm:flex-row gap-3">
                      <a
                      href="mailto:salvadorjaneiro02@gmail.com"
                      className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-all"
                    >
                      📧 Email Me
                    </a>
                    <a
                      href="https://www.linkedin.com/in/salvador-janeiro-419010296"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-all"
                    >
                      💼 LinkedIn
                    </a>
                    </div>
                  </div> 
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
          <button
            onClick={exportReport}
            className="flex items-center justify-center space-x-2 bg-green-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-green-700 transition-all shadow-lg hover:scale-105"
          >
            <Download size={20} />
            <span>Download Full Report</span>
          </button>
          
          <button
            onClick={onReset}
            className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg hover:scale-105"
          >
            <Leaf size={20} />
            <span>Take Another Assessment</span>
          </button>
        </div>

        {/* Methodology Note */}
        <div className="bg-gray-50 rounded-xl p-6 text-sm text-gray-600">
          <h4 className="font-semibold text-gray-800 mb-2">Methodology & Data Sources</h4>
          <p className="mb-2">
            This assessment uses emission factors from the EPA, IPCC, and peer-reviewed research. 
            Carbon calculations are based on EU averages and international standards. Water footprint 
            data comes from the Water Footprint Network. Plastic waste estimates use global consumption 
            averages.
          </p>
          <p>
            The sustainability score is calculated using weighted factors: Carbon (40%), Water (25%), 
            Plastic (20%), and Lifestyle Habits (15%). Benchmarks include the UN 1.5°C target (2.5 tons 
            CO₂/year) and EU averages.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8">
          <p>Created by Salvador Janeiro</p>
        </div>
      </div>
    </div>
  );
};

export default IndividualResults;
